<script setup lang="ts">
import MainLayout from '@/components/layout/MainLayout.vue'
import { Button } from '@/components/ui/button'
import { useRouter } from 'vue-router'
import { useForm11 } from '@/composables/useForm11'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { ref, computed } from 'vue'

interface Option {
  id: string
  label: string
  route: string
  options?: Record<string, string>
}

const router = useRouter()
const form11 = useForm11()
const selectedItems = ref<string[]>([])

// Detect if there is unsubmitted saved progress
const hasSavedProgress = computed(() => form11.hasSavedProgress())

const categories: Record<
  string,
  { title: string; icon: string; options: Option[] }
> = {
  health: {
    title: 'Health',
    icon: '/icons/health.png',
    options: [
      {
        id: 'assistance-dog',
        label: 'Assistance Dog Allowance',
        route: '/form11/assistance-dog-allowance',
      },
      {
        id: 'guide-dog',
        label: 'Guide Dog Allowance',
        route: '/form11/guide-dog-allowance',
      },
      {
        id: 'income-continuance',
        label: 'Income Continuance',
        route: '/form11/income-continuance',
      },
      {
        id: 'nursing-home',
        label: 'Nursing Home Expenses',
        route: '/form11/nursing-home-expenses',
      },
      {
        id: 'blind-person',
        label: "Blind Person's Tax Credit",
        route: '/form11/blind-person-tax-credit',
      },
      {
        id: 'health-expenses',
        label: 'Health Expenses',
        route: '/form11/health-expenses',
      },
      {
        id: 'medical-insurance',
        label: 'Medical Insurance Relief',
        route: '/form11/medical-insurance-relief',
      },
    ],
  },
  family: {
    title: 'You & Your Family',
    icon: '/icons/yourFamily.png',
    options: [
      {
        id: 'dependant-relevant',
        label: 'Dependant Relevant Tax Credit',
        route: '/form11/dependant-relevant-tax-credit',
      },
      {
        id: 'incapacitated-child',
        label: 'Incapacitated Child Tax Credit',
        route: '/form11/incapacitated-child-tax-credit',
      },
      {
        id: 'maintenance-payment',
        label: 'Relief for Maintenance Payment',
        route: '/form11/maintenance-payment-relief',
      },
      {
        id: 'single-person-child',
        label: 'Single Person Child Carer Credit',
        route: '/form11/single-person-child-carer-credit',
      },
      {
        id: 'rent-tax',
        label: 'Rent Tax Credit',
        route: '/form11/rent-tax-credit',
      },
      {
        id: 'tuition-fees',
        label: 'Tuition Fees',
        route: '/form11/tuition-fees',
      },
    ],
  },
  job: {
    title: 'Your Job',
    icon: '/icons/yourJob.png',
    options: [
      {
        id: 'additional-voluntary',
        label: 'Additional Voluntary Contributions',
        route: '/form11/additional-voluntary-contributions',
      },
      {
        id: 'flat-rate',
        label: 'Flat Rate Expenses',
        route: '/form11/flat-rate-expenses',
      },
      {
        id: 'personal-retirement',
        label: 'Personal Retirement Savings Account',
        route: '/form11/personal-retirement-savings',
      },
      {
        id: 'remote-working',
        label: 'Remote Working Relief',
        route: '/form11/remote-working-relief',
      },
      {
        id: 'retirement-annuity',
        label: 'Retirement Annuity Relief',
        route: '/form11/retirement-annuity-relief',
      },
    ],
  },
  other: {
    title: 'Other',
    icon: '/icons/other.svg',
    options: [
      {
        id: 'fisher-tax-credit',
        label: 'Fisher Tax Credit',
        route: '/form11/fisher-tax-credit',
      },
      {
        id: 'seafarer-allowance',
        label: 'Seafarer Allowance',
        route: '/form11/seafarer-allowance',
      },
      {
        id: 'naval-service-credit',
        label: 'Naval Service Credit',
        route: '/form11/naval-service-credit',
      },
      {
        id: 'owner-occupier-relief',
        label: 'Owner Occupier Relief',
        route: '/form11/owner-occupier-relief',
      },
      {
        id: 'home-renovation-incentive',
        label: 'Home Renovation Incentive',
        route: '/form11/home-renovation-incentive',
      },
      {
        id: 'approved-sports-bodies',
        label: 'Approved Sports Bodies',
        route: '/form11/approved-sports-bodies',
      },
      {
        id: 'startup-relief-entrepreneurs',
        label: 'Startup Relief for Entrepreneurs',
        route: '/form11/startup-relief-entrepreneurs',
      },
      {
        id: 'employment-investment-incentive',
        label: 'Employment Investment Incentive',
        route: '/form11/employment-investment-incentive',
      },
      {
        id: 'mortgage-interest-tax-credit',
        label: 'Mortgage Interest Tax Credit',
        route: '/form11/mortgage-interest-tax-credit',
      },
      {
        id: 'employee-tax-credit',
        label: 'Employee Tax Credit',
        route: '/form11/employee-tax-credit',
      },
      {
        id: 'earned-income-tax-credit',
        label: 'Earned Income Tax Credit',
        route: '/form11/earned-income-tax-credit',
      },
    ],
  },
  otherIncome: {
    title: 'Other Income',
    icon: '/icons/other-incomes.png',
    options: [
      {
        id: 'canadian-dividends',
        label: 'Canadian Dividends',
        route: '/form11/final-form',
        options: { incomeId: 'canadian-dividends' },
      },
      {
        id: 'dividends-distributions',
        label: 'Dividends & Distributions',
        route: '/form11/final-form',
        options: { incomeId: 'dividends-distributions' },
      },
      {
        id: 'emp-off-pen',
        label: 'Employment/Office/Pension not subject to PAYE',
        route: '/form11/final-form',
        options: { incomeId: 'emp-off-pen' },
      },
      {
        id: 'fees-commissions',
        label: 'Fees and Commissions',
        route: '/form11/final-form',
        options: { incomeId: 'fees-commissions' },
      },
      {
        id: 'foreign-pensions',
        label: 'Foreign Pensions',
        route: '/form11/final-form',
        options: { incomeId: 'foreign-pensions' },
      },
      {
        id: 'foreign-salary',
        label: 'Foreign Salary',
        route: '/form11/final-form',
        options: { incomeId: 'foreign-salary' },
      },
      {
        id: 'maintenance-payments',
        label: 'Maintenance Payments',
        route: '/form11/final-form',
        options: { incomeId: 'maintenance-payments' },
      },
      {
        id: 'rental-income',
        label: 'Rental Income',
        route: '/form11/final-form',
        options: { incomeId: 'rental-income' },
      },
      {
        id: 'trading-profit',
        label: 'Trading Profit or Professional Income',
        route: '/form11/final-form',
        options: { incomeId: 'trading-profit' },
      },
      {
        id: 'uk-dividends',
        label: 'UK Dividends',
        route: '/form11/final-form',
        options: { incomeId: 'uk-dividends' },
      },
      {
        id: 'us-dividends',
        label: 'US Dividends',
        route: '/form11/final-form',
        options: { incomeId: 'us-dividends' },
      },
      {
        id: 'untaxed-state-income',
        label: 'Untaxed State Income',
        route: '/form11/final-form',
        options: { incomeId: 'untaxed-state-income' },
      },
      {
        id: 'irish-taxed-income',
        label: 'Irish Taxed Income',
        route: '/form11/final-form',
        options: { incomeId: 'irish-taxed-income' },
      },
    ],
  },
}

const toggleSelection = (id: string) => {
  const index = selectedItems.value.indexOf(id)
  if (index > -1) {
    selectedItems.value.splice(index, 1)
  } else {
    selectedItems.value.push(id)
  }
}

const handleContinue = () => {
  if (selectedItems.value.length === 0) {
    alert('Please select at least one option to continue')
    return
  }

  const allOptions = Object.values(categories).flatMap((cat) => cat.options)
  const chosenOptions = allOptions.filter((opt) =>
    selectedItems.value.includes(opt.id),
  )

  const routes: string[] = []
  const incomeIds: string[] = []

  chosenOptions.forEach((opt) => {
    if (opt.options?.incomeId) {
      incomeIds.push(opt.options.incomeId)
    } else {
      routes.push(opt.route)
    }
  })

  // If any income types were selected, include the unified income form route
  if (incomeIds.length > 0 && !routes.includes('/form11/final-form')) {
    routes.push('/form11/final-form')
  }

  if (incomeIds.length > 0) {
    form11.saveSelectedIncomes(incomeIds)
  }

  // Initialise Form 11 stepper with the selected routes
  form11.setup(routes)
}
</script>

<template>
  <MainLayout>
    <div class="space-y-6 px-6 pb-24">
      <h1 class="text-2xl font-bold mt-8 text-center">Tax Refund</h1>
      <Button
        size="lg"
        class="w-full"
        @click="router.push('/form11-questionnaire')"
      >
        Questionnaire
      </Button>

      <!-- Continue previous application button -->
      <Button
        v-if="hasSavedProgress"
        size="lg"
        class="w-full bg-maximo-green hover:bg-maximo-green/90"
        @click="form11.resume()"
      >
        Continue previous application
      </Button>

      <!-- Advanced Use Accordion -->
      <Accordion type="single" collapsible class="w-full">
        <AccordionItem value="advanced-use">
          <AccordionTrigger class="text-lg font-medium">
            <div class="w-full flex justify-between items-center pr-4">
              <span>For Advanced Use</span>
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <!-- Inner categories accordion -->
            <Accordion type="single" collapsible class="w-full">
              <AccordionItem
                v-for="(category, key) in categories"
                :key="key"
                :value="key"
                class="mb-4 border-none"
              >
                <AccordionTrigger
                  class="bg-white rounded-lg px-4 min-h-[80px] hover:no-underline shadow-md border-2 border-transparent data-[state=open]:border-maximo-green"
                >
                  <div class="flex items-center gap-5">
                    <img
                      :src="category.icon"
                      :alt="category.title"
                      class="w-8 h-8 object-contain"
                    />
                    <span class="text-lg font-semibold text-maximo-text">{{
                      category.title
                    }}</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div class="grid grid-cols-1 gap-2 pt-4">
                    <button
                      v-for="option in category.options"
                      :key="option.id"
                      class="flex items-center justify-between bg-white shadow-sm rounded-xl px-4 py-3 min-h-[56px] hover:bg-gray-50"
                      @click="toggleSelection(option.id)"
                    >
                      <span class="text-maximo-text font-semibold">{{
                        option.label
                      }}</span>
                      <i-ion-checkbox
                        v-if="selectedItems.includes(option.id)"
                        class="w-6 h-6 text-maximo-teal"
                      />
                      <i-ion-checkbox-outline
                        v-else
                        class="w-6 h-6 text-maximo-teal"
                      />
                    </button>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <Button size="lg" class="w-full mt-4" @click="handleContinue">
              Continue
            </Button>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  </MainLayout>
</template>
